import axios from 'axios';
import {v4 as uuid} from 'uuid';
import reactQueryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import assetUpdaters from '@/api/updaters/content-studio/asset';
import {ASSETS, CONTENT_LAB} from '@/hooks/content-studio/consts';
import {
  contentStudioActions,
  FileObject,
  FileStatus,
  ErrorKind,
} from '@/stores/contentStudioStore';

export const getFileMetadata = (
  file: File
): Promise<{
  duration_millis: number;
  width: number;
  height: number;
}> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        duration_millis: Math.round(video.duration * 1000),
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };

    video.src = url;
  });
};

export const shortenFileName = (fileName: string) => {
  return fileName.length > 13 ? `${fileName.slice(0, 13)}...` : fileName;
};

const MAX_VIDEO_DURATION = 180000;
const MAX_FILE_SIZE = 2 ** 30;
// 1GB in powers of 2 (not 10)

export const onFileInput = async (
  acceptedFiles: File[],
  analyticsInteractionID: string,
  analyticsLocation: string,
  processEventHandlers: {
    started: (params: {
      endpoint: string;
      asset_id?: string;
      original_id?: string;
      interaction_id: string;
      process_details?: string;
      process_id: string;
      process_name: string;
      source?: string;
    }) => void;
    ended: (params: {
      endpoint: string;
      outcome_id?: string;
      original_id?: string;
      error?: string;
      process_details?: string;
      process_id: string;
      process_name: string;
      result: string;
    }) => void;
  }
) => {
  const {started, ended} = processEventHandlers;
  const uploadProcessId = uuid();
  const DEFAULT_ERROR_MESSAGE = translate('Failed to upload');
  const handleFileErrors = (
    name: string,
    errorToReport: string,
    errorKind: ErrorKind,
    errors: string[]
  ) => {
    errors.forEach((error) => {
      contentStudioActions.addError({
        title: error,
        description: name,
        kind: errorKind,
      });
    });
    ended({
      endpoint: '',
      process_id: uploadProcessId,
      process_name: 'upload',
      original_id: name,
      outcome_id: name,
      result: 'failure',
      error: errorToReport,
    });
  };

  const getSignedUrls = async (filesToUpload: FileObject[]) => {
    try {
      const response = await assetUpdaters.getSignedUrls(
        filesToUpload.map((f) => ({
          file: f.file,
          metadata: f.metadata,
        }))
      );
      const updatedFiles = filesToUpload.map((file) => {
        contentStudioActions.updateFile({
          name: file.file.name,
          id: response.urls[file.file.name].asset_id,
          status: FileStatus.IN_PROGRESS,
        });
        return {
          ...file,
          signedUrl: response.urls[file.file.name].upload_url,
          id: response.urls[file.file.name].asset_id,
        };
      });

      return updatedFiles;
    } catch (error) {
      filesToUpload.forEach((f) => {
        const errorToReport = 'Failed to get upload URL';
        contentStudioActions.updateFile({
          name: f.file.name,
          status: FileStatus.ERROR,
          error: errorToReport,
          progress: 0,
        });
        handleFileErrors(f.file.name, errorToReport, ErrorKind.FAILURE, [
          DEFAULT_ERROR_MESSAGE,
        ]);
      });
      return [];
    }
  };

  const handleUpload = async (newFiles: FileObject[]) => {
    const filesWithSignedUrls = await getSignedUrls(newFiles);
    contentStudioActions.addSelectedAssets(
      filesWithSignedUrls.map((fileObj) => ({
        id: fileObj.id || '',
        isUploading: true,
        type: 'Uploads',
        fileName: fileObj.file.name,
        durationMillis: fileObj.metadata.duration_millis,
      }))
    );
    const uploadResults = await Promise.allSettled(
      filesWithSignedUrls.map((fileObj) => {
        return uploadFileToGCS(fileObj);
      })
    );
    uploadResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        contentStudioActions.updateFile({
          name: newFiles[index].file.name,
          status: FileStatus.ERROR,
          error: 'Upload failed',
        });
        handleFileErrors(
          newFiles[index].file.name,
          'Upload failed',
          ErrorKind.FAILURE,
          [DEFAULT_ERROR_MESSAGE]
        );
      } else if (result.status === 'fulfilled') {
        contentStudioActions.updateFile({
          name: newFiles[index].file.name,
          status: FileStatus.SUCCESS,
        });
        const outcomeId =
          filesWithSignedUrls.find(
            (fileObj) => fileObj.file.name === newFiles[index].file.name
          )?.id || '';
        ended({
          endpoint: '',
          process_id: uploadProcessId,
          process_name: 'upload',
          original_id: newFiles[index].file.name,
          outcome_id: outcomeId,
          result: 'success',
          process_details: JSON.stringify({
            duration: newFiles[index].metadata.duration_millis,
            width: newFiles[index].metadata.width,
            height: newFiles[index].metadata.height,
            size: newFiles[index].file.size,
          }),
        });
      }
    });
    reactQueryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, ASSETS],
    });
  };

  const uploadFileToGCS = async (fileObj: {signedUrl: string; file: File}) => {
    try {
      await axios.put(fileObj.signedUrl, fileObj.file, {
        headers: {
          'Content-Type': fileObj.file.type,
        },
        onUploadProgress: (progress) => {
          contentStudioActions.updateFile({
            name: fileObj.file.name,
            status: FileStatus.IN_PROGRESS,
            progress: progress.progress
              ? Math.round(progress.progress * 100)
              : 0,
          });
        },
      });
      return true;
    } catch (error) {
      const errorToReport = 'Failed to upload file to GCS';
      contentStudioActions.updateFile({
        name: fileObj.file.name,
        status: FileStatus.ERROR,
        error: errorToReport,
      });
      handleFileErrors(fileObj.file.name, errorToReport, ErrorKind.FAILURE, [
        DEFAULT_ERROR_MESSAGE,
      ]);
      throw error;
    }
  };

  acceptedFiles.forEach((file) => {
    started({
      endpoint: '',
      process_name: 'upload',
      original_id: file.name,
      asset_id: file.name,
      process_id: uploadProcessId,
      interaction_id: analyticsInteractionID,
      source: analyticsLocation,
    });
  });
  const metadata = await Promise.all(
    acceptedFiles.map((file) => getFileMetadata(file))
  );
  const newFiles: FileObject[] = acceptedFiles.map((file, index) => ({
    file,
    id: null,
    status: FileStatus.IN_PROGRESS,
    progress: 0,
    error: undefined,
    metadata: metadata[index],
  }));
  newFiles.forEach((file) => {
    const errorsToReport = [];
    const errorMessages = [];
    if (file.file.size > MAX_FILE_SIZE) {
      file.status = FileStatus.ERROR;
      file.error = 'File is too large';
      errorsToReport.push(translate('File is too large'));
      errorMessages.push(
        translate('Your video is too large, please upload files up to 1 GB')
      );
    }
    if (file.metadata.duration_millis > MAX_VIDEO_DURATION) {
      file.status = FileStatus.ERROR;
      file.error = 'Video is too long';
      errorsToReport.push(translate('Video is too long'));
      errorMessages.push(
        translate('Your video is too long, please upload files up to 3 mins')
      );
    }
    if (errorsToReport.length > 0) {
      const errorToReport = errorsToReport.join(', ');
      handleFileErrors(
        file.file.name,
        errorToReport,
        ErrorKind.VALIDATION,
        errorMessages
      );
    }
  });
  const filteredFiles = newFiles.filter(
    (file) => file.status !== FileStatus.ERROR
  );

  if (filteredFiles.length > 0) {
    contentStudioActions.addFiles(filteredFiles);
    handleUpload(filteredFiles);
    reactQueryClient.invalidateQueries({
      queryKey: [CONTENT_LAB, ASSETS],
    });
  }
};
